import { useEffect, useState } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { Check, Globe, Loader2, Trash2, X } from "lucide-react"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  EmailSourceJson,
  ExampleEmailJson,
  createEmailSource,
  deleteEmailSource,
  searchExampleEmails,
  updateEmailSource,
} from "@/api/email_source"
import { format_custom_deduction_method_label } from "@/utils/format_custom_deduction_method"
import { toast } from "@/components/ui/use-toast"
import { use_debounced_value } from "@/hooks/use_debounced_value"
import { UserState } from "@/auth/user"

const METHOD_OPTIONS = ["DEDUCT", "Pay Customer Directly", "INDIRECT"]
const NO_METHOD = "__none__"
const SEARCH_DEBOUNCE_MS = 250

/** Strip reply/forward prefixes so a picked example subject becomes a reusable pattern. */
export function clean_subject_pattern(subject: string): string {
  let s = subject.trim()
  const prefix = /^(re|fw|fwd)\s*:\s*/i
  while (prefix.test(s)) {
    s = s.replace(prefix, "")
  }
  return s.trim()
}

/** Append a chip entry, ignoring blanks and case-insensitive duplicates. */
export function add_entry(list: string[], raw: string): string[] {
  const entry = raw.trim()
  if (entry === "") return list
  if (list.some((e: string) => e.toLowerCase() === entry.toLowerCase())) return list
  return [...list, entry]
}

/** The "@domain.com" entry for a full address, or null when it isn't one. */
export function domain_entry_for(email: string): string | null {
  const at = email.lastIndexOf("@")
  if (at <= 0 || at === email.length - 1) return null
  return "@" + email.slice(at + 1).toLowerCase()
}

/** Combine stored emails + domains back into the mixed chip list the dialog edits. */
export function sender_entries_of(source: EmailSourceJson): string[] {
  return [...source.sender_emails, ...source.sender_domains.map((d: string) => "@" + d)]
}

interface ChipListProps {
  entries: string[]
  placeholder: string
  disabled?: boolean
  onChange: (entries: string[]) => void
}

/** Text input + removable chips, entries added on Enter or the Add button. */
function ChipList({ entries, placeholder, disabled, onChange }: ChipListProps) {
  const [input, setInput] = useState("")

  function commit() {
    const next = add_entry(entries, input)
    if (next !== entries) onChange(next)
    setInput("")
  }

  return (
    <div class="space-y-2">
      {!disabled && (
        <div class="flex gap-2">
          <Input
            value={input}
            placeholder={placeholder}
            onInput={(e: Event) => setInput((e.target as HTMLInputElement).value)}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key === "Enter") {
                e.preventDefault()
                commit()
              }
            }}
          />
          <Button type="button" variant="outline" onClick={commit} disabled={input.trim() === ""}>
            Add
          </Button>
        </div>
      )}
      {entries.length > 0 && (
        <div class="flex flex-wrap gap-1.5">
          {entries.map((entry: string) => (
            <span key={entry} class="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs rounded-full px-2.5 py-1">
              {entry}
              {!disabled && (
                <button
                  type="button"
                  class="text-gray-400 hover:text-gray-700"
                  onClick={() => onChange(entries.filter((e: string) => e !== entry))}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/** An email to seed a new source from ("create source from this email"). */
export interface EmailSourceTemplate {
  message_id: string
  from_email: string | null
  subject: string
}

interface DistributorSourceDialogProps {
  open: boolean
  /** null = create a new source; otherwise edit/view this one. */
  source: EmailSourceJson | null
  /** Create mode only: prefill sender/subject/example from this email. */
  template?: EmailSourceTemplate | null
  onClose: () => void
  /** Called after any save/delete so the caller can revalidate the source list. */
  onSaved: () => void
}

/**
 * Create or edit a distributor email data source: who the emails come from, what the
 * subjects look like, the default deduction method, and where disputes / backup
 * requests go. Users can search their synced emails for an example and prefill the
 * sender/subject metadata from it.
 *
 * Brand sources are org-scoped; system admins can upgrade one to a global source that
 * every brand sees. Global sources are read-only for brand users.
 */
export function DistributorSourceDialog({ open, source, template, onClose, onSaved }: DistributorSourceDialogProps) {
  const user = UserState.use()
  const is_system_admin = user?.role === "system_admin"
  // Own-org rows are editable by the brand; global rows only by system admins.
  const editable = source === null || !source.is_global || is_system_admin

  const [name, setName] = useState("")
  const [method, setMethod] = useState(NO_METHOD)
  const [senderEntries, setSenderEntries] = useState<string[]>([])
  const [subjectPatterns, setSubjectPatterns] = useState<string[]>([])
  const [disputeEmail, setDisputeEmail] = useState("")
  const [backupEmail, setBackupEmail] = useState("")
  const [exampleMessageId, setExampleMessageId] = useState<string | null>(null)
  const [exampleQuery, setExampleQuery] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName(source?.name ?? "")
    setMethod(source?.default_custom_deduction_method || NO_METHOD)
    setSubjectPatterns(source?.subject_patterns ?? [])
    setDisputeEmail(source?.dispute_email ?? "")
    setBackupEmail(source?.backup_request_email ?? "")
    setExampleQuery("")
    setError(null)
    if (source) {
      setSenderEntries(sender_entries_of(source))
      setExampleMessageId(source.example_message_id)
    } else if (template) {
      // "Create source from this email": the email is the example, its sender and
      // cleaned subject become the first patterns.
      setSenderEntries(template.from_email ? [template.from_email.toLowerCase()] : [])
      const subject = clean_subject_pattern(template.subject)
      setSubjectPatterns(subject ? [subject] : [])
      setExampleMessageId(template.message_id)
    } else {
      setSenderEntries([])
      setExampleMessageId(null)
    }
  }, [open, source?.id, template?.message_id])

  const debounced_query = use_debounced_value(exampleQuery, SEARCH_DEBOUNCE_MS)
  const { data: examples, isLoading: searching } = useSWR<ExampleEmailJson[]>(
    open && editable && debounced_query.trim() !== "" ? `/email-source/example-emails?q=${encodeURIComponent(debounced_query)}` : null,
    () => searchExampleEmails(debounced_query)
  )

  /** Fill sender + subject metadata from a picked example email. */
  function apply_example(email: ExampleEmailJson) {
    setExampleMessageId(email.message_id)
    if (email.from_email) {
      setSenderEntries((entries: string[]) => add_entry(entries, email.from_email!.toLowerCase()))
    }
    const subject = clean_subject_pattern(email.subject)
    if (subject !== "") {
      setSubjectPatterns((patterns: string[]) => add_entry(patterns, subject))
    }
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const req = {
        name,
        default_custom_deduction_method: method === NO_METHOD ? "" : method,
        sender_entries: senderEntries,
        subject_patterns: subjectPatterns,
        dispute_email: disputeEmail.trim(),
        backup_request_email: backupEmail.trim(),
        ...(exampleMessageId ? { example_message_id: exampleMessageId } : {}),
      }
      if (source) {
        await updateEmailSource(source.id, req)
        toast({ title: `Updated ${name}` })
      } else {
        await createEmailSource(req)
        toast({ title: `Added ${name}`, description: "This source is now available when importing files." })
      }
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the source")
    } finally {
      setSaving(false)
    }
  }

  async function handleMakeGlobal() {
    if (!source) return
    setSaving(true)
    setError(null)
    try {
      await updateEmailSource(source.id, { make_global: true })
      toast({ title: `${source.name} is now a global source`, description: "All brands will see it as an option." })
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not make the source global")
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!source) return
    setSaving(true)
    setError(null)
    try {
      await deleteEmailSource(source.id)
      toast({ title: `Deleted ${source.name}` })
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete the source")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o: boolean) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{source ? (editable ? `Edit ${source.name}` : source.name) : "Add distributor source"}</DialogTitle>
          <p class="text-sm text-gray-500">
            Describe a distributor that sends deduction data by email. This is stored as reference
            metadata for now — automatic email matching and dispute filing for a new source still
            need to be built out on our side.
          </p>
        </DialogHeader>

        <div class="space-y-4">
          {source?.is_global && !is_system_admin && (
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              This is a global source maintained by MarginWiz and can't be edited here.
            </div>
          )}

          <div class="grid sm:grid-cols-2 gap-4">
            <div>
              <Label class="text-sm font-medium mb-1.5 block">Name</Label>
              <Input
                value={name}
                placeholder="e.g. Rainforest"
                disabled={!editable}
                onInput={(e: Event) => setName((e.target as HTMLInputElement).value)}
              />
            </div>
            <div>
              <Label class="text-sm font-medium mb-1.5 block">Default deduction method</Label>
              <Select value={method} onValueChange={setMethod} disabled={!editable}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_METHOD}>—</SelectItem>
                  {METHOD_OPTIONS.map((m: string) => (
                    <SelectItem key={m} value={m}>
                      {format_custom_deduction_method_label(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {editable && (
            <div>
              <Label class="text-sm font-medium mb-1.5 block">Fill from an example email</Label>
              <p class="text-xs text-gray-500 mb-2">
                Search your synced emails for one from this distributor — picking it fills the sender and
                subject fields below.
              </p>
              <Input
                value={exampleQuery}
                placeholder="Search synced emails by sender or subject…"
                onInput={(e: Event) => setExampleQuery((e.target as HTMLInputElement).value)}
              />
              {exampleQuery.trim() !== "" && (
                <div class="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                  {searching ? (
                    <div class="p-3 text-sm text-gray-500 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                    </div>
                  ) : (examples ?? []).length === 0 ? (
                    <div class="p-3 text-sm text-gray-500">No synced emails match that search.</div>
                  ) : (
                    (examples ?? []).map((email: ExampleEmailJson) => (
                      <button
                        key={email.message_id}
                        type="button"
                        class={`w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-gray-50 ${
                          exampleMessageId === email.message_id ? "bg-blue-50" : ""
                        }`}
                        onClick={() => apply_example(email)}>
                        <Check
                          className={`h-4 w-4 mt-0.5 shrink-0 ${
                            exampleMessageId === email.message_id ? "text-blue-600" : "text-transparent"
                          }`}
                        />
                        <span class="min-w-0 flex-1">
                          <span class="block text-sm font-medium text-gray-900 truncate">{email.subject || "(no subject)"}</span>
                          <span class="block text-xs text-gray-500 truncate">
                            {email.from_email ?? email.from_name ?? "unknown sender"} ·{" "}
                            {format(parseISO(email.date), "MMM d, yyyy")}
                          </span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <Label class="text-sm font-medium mb-1.5 block">Emails come from</Label>
            <p class="text-xs text-gray-500 mb-2">
              Add full addresses (ap@unfi.com) or whole domains (@unfi.com) — a domain matches every
              sender at that company.
            </p>
            <ChipList
              entries={senderEntries}
              placeholder="ap@distributor.com or @distributor.com"
              disabled={!editable}
              onChange={setSenderEntries}
            />
          </div>

          <div>
            <Label class="text-sm font-medium mb-1.5 block">Subject lines look like</Label>
            <p class="text-xs text-gray-500 mb-2">
              Phrases that appear in this distributor's email subjects, e.g. "has submitted electronic payment".
            </p>
            <ChipList
              entries={subjectPatterns}
              placeholder="e.g. Payment Advice Note"
              disabled={!editable}
              onChange={setSubjectPatterns}
            />
          </div>

          <div class="grid sm:grid-cols-2 gap-4">
            <div>
              <Label class="text-sm font-medium mb-1.5 block">Send disputes to</Label>
              <Input
                value={disputeEmail}
                placeholder="disputes@distributor.com"
                disabled={!editable}
                onInput={(e: Event) => setDisputeEmail((e.target as HTMLInputElement).value)}
              />
            </div>
            <div>
              <Label class="text-sm font-medium mb-1.5 block">Send backup requests to</Label>
              <Input
                value={backupEmail}
                placeholder="Same as disputes if blank"
                disabled={!editable}
                onInput={(e: Event) => setBackupEmail((e.target as HTMLInputElement).value)}
              />
            </div>
          </div>
          <p class="text-xs text-gray-500 -mt-2">
            These addresses are stored for now so we know where this distributor handles disputes and
            backup requests. Actually sending disputes by email to a new source requires code changes on
            our side — reach out and we'll wire it up.
          </p>

          {source?.notes && <p class="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">{source.notes}</p>}

          {error && <div class="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <div class="flex gap-2">
            {source && editable && !source.is_global && (
              <Button variant="outline" className="text-red-600 gap-1.5" disabled={saving} onClick={() => void handleDelete()}>
                <Trash2 className="h-4 w-4" /> Delete
              </Button>
            )}
            {source && is_system_admin && !source.is_global && (
              <Button variant="outline" className="gap-1.5" disabled={saving} onClick={() => void handleMakeGlobal()} title="Make this source visible to all brands">
                <Globe className="h-4 w-4" /> Make global
              </Button>
            )}
          </div>
          <div class="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              {editable ? "Cancel" : "Close"}
            </Button>
            {editable && (
              <Button disabled={saving || name.trim() === ""} onClick={() => void handleSave()}>
                {saving ? "Saving…" : source ? "Save" : "Add source"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("clean_subject_pattern", () => {
    it("strips stacked reply/forward prefixes", () => {
      expect(clean_subject_pattern("FW: RE: Rainforest has submitted electronic payment")).toBe(
        "Rainforest has submitted electronic payment"
      )
      expect(clean_subject_pattern("Fwd: Payment Advice Note")).toBe("Payment Advice Note")
      expect(clean_subject_pattern("  Regular subject  ")).toBe("Regular subject")
      // "Regarding" is not a prefix
      expect(clean_subject_pattern("Regarding: your invoice")).toBe("Regarding: your invoice")
    })
  })

  describe("add_entry", () => {
    it("ignores blanks and case-insensitive duplicates", () => {
      expect(add_entry([], "  ap@unfi.com ")).toEqual(["ap@unfi.com"])
      expect(add_entry(["ap@unfi.com"], "AP@UNFI.COM")).toEqual(["ap@unfi.com"])
      expect(add_entry(["a"], "   ")).toEqual(["a"])
    })
  })

  describe("domain_entry_for", () => {
    it("extracts the @domain chip from a full address", () => {
      expect(domain_entry_for("AP@Unfi.com")).toBe("@unfi.com")
      expect(domain_entry_for("not-an-email")).toBeNull()
      expect(domain_entry_for("trailing@")).toBeNull()
      expect(domain_entry_for("@unfi.com")).toBeNull()
    })
  })

  describe("sender_entries_of", () => {
    it("merges emails and prefixed domains", () => {
      const source = {
        sender_emails: ["ap@unfi.com"],
        sender_domains: ["unfi.com", "sasrecovery.com"],
      } as EmailSourceJson
      expect(sender_entries_of(source)).toEqual(["ap@unfi.com", "@unfi.com", "@sasrecovery.com"])
    })
  })
}
